import { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useNotificationContext } from '@context/NotificationsContext';
import { useDesktop } from '@utils/hooks/useDesktop';
import { NOTIFICATION_NAMES_MAP } from '@utils/constants';
import type { PlickNotification } from '@interfaces/general';

const LegacyCard = ({
  title,
  subtitle,
  description,
  body,
  icon,
  accentColor,
  onAccept,
  onReject,
  acceptLabel,
  rejectLabel,
  autoCloseTimer,
}: PlickNotification) => {
  const isDesktop = useDesktop();
  const timeOutBar = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      if (timeOutBar.current) timeOutBar.current.style.width = '0%';
    }, 100);
  }, []);

  return (
    <div className="rounded-lg bg-color-white px-3 py-3" style={{ boxShadow: 'rgba(0,0,0,0.25) 0px 0px 10px 5px' }}>
      <div className={`flex ${isDesktop ? 'flex-row' : 'flex-col'}`}>
        <div className="flex flex-1 items-center">
          <div
            className="w-[41px] min-w-[41px] h-10 rounded-[50%] flex items-center justify-center"
            style={{ backgroundColor: accentColor }}
          >
            <Icon icon={icon} color="#ffffff" width="28px" height="28px" />
          </div>
          <div className="ml-3">
            <p className="text-lg font-semibold">
              {title} {subtitle ? <b>{subtitle}</b> : null}
            </p>
            <p>{description}</p>
            {body ?? null}
          </div>
        </div>
        {(onAccept || onReject) && (
          <div
            className="flex flex-col justify-center"
            style={{
              borderLeft: isDesktop ? '1px solid #C0C0C0' : 'none',
              width: isDesktop ? '135px' : '100%',
              marginTop: isDesktop ? '0px' : '12px',
              paddingLeft: isDesktop ? '12px' : '0px',
            }}
          >
            {onAccept && (
              <button
                className="font-bold text-color-white py-2 rounded-md flex items-center justify-center mb-1"
                style={{ backgroundColor: accentColor }}
                onClick={() => onAccept()}
              >
                {acceptLabel ? acceptLabel.toUpperCase() : 'ACEPTAR'}
                <Icon className="ml-1" icon="mingcute:check-fill" color="#ffffff" />
              </button>
            )}
            {onReject && (
              <button
                className="font-bold py-2 flex items-center justify-center"
                onClick={() => onReject()}
                style={{ color: '#FF3939', marginTop: isDesktop ? '0' : '4px' }}
              >
                {rejectLabel ? rejectLabel.toUpperCase() : 'RECHAZAR'}
                <Icon className="ml-1" icon="ep:close-bold" color="#FF3939" />
              </button>
            )}
          </div>
        )}
      </div>
      <div
        ref={autoCloseTimer ? timeOutBar : null}
        className="h-2 rounded-md"
        style={{
          marginTop: '8px',
          backgroundColor: accentColor,
          width: '100%',
          transition: autoCloseTimer ? `width ${autoCloseTimer}ms ease-in-out` : 'none',
        }}
      />
    </div>
  );
};

export const NotificationCenterV2 = () => {
  const isDesktop = useDesktop();
  const { notifications } = useNotificationContext();
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerBarRef = useRef<HTMLDivElement>(null);

  const incomingOrders = notifications.filter((n) => n.id.endsWith(NOTIFICATION_NAMES_MAP.INCOMING_ORDER.key));
  const otherNotifications = notifications.filter((n) => !n.id.endsWith(NOTIFICATION_NAMES_MAP.INCOMING_ORDER.key));

  useEffect(() => {
    if (incomingOrders.length === 0) {
      setCurrentIndex(0);
    } else if (currentIndex >= incomingOrders.length) {
      setCurrentIndex(incomingOrders.length - 1);
    }
  }, [incomingOrders.length, currentIndex]);

  const current = incomingOrders[currentIndex];

  useEffect(() => {
    if (!timerBarRef.current) return;
    timerBarRef.current.style.transition = 'none';
    timerBarRef.current.style.width = '100%';
    const id = setTimeout(() => {
      if (!timerBarRef.current) return;
      const duration = current?.autoCloseTimer ?? 30000;
      timerBarRef.current.style.transition = `width ${duration}ms linear`;
      timerBarRef.current.style.width = '0%';
    }, 100);
    return () => clearTimeout(id);
  }, [current?.id]);
  const total = incomingOrders.length;

  if (notifications.length === 0) return null;

  return (
    <div
      className={`fixed bottom-0 right-0 z-[99] flex flex-col gap-3 ${
        isDesktop ? 'w-[380px] mr-5 mb-5' : 'w-full px-3 pb-[70px]'
      }`}
      style={{ maxHeight: 'calc(100vh - 20px)', overflowY: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none' }}
    >
      {/* Legacy notifications (RIDER_ARRIVED, ORDER_DELIVERED, etc.) */}
      {otherNotifications.map((n) => (
        <LegacyCard key={n.id} {...n} />
      ))}

      {/* Paginated incoming-order widget */}
      {total > 0 && current && (
        <div className="rounded-[10px] overflow-hidden" style={{ boxShadow: 'rgba(37,55,70,0.22) 0px 10px 30px 0px' }}>
          {/* Red header bar */}
          <div
            className="flex items-center justify-between px-4 py-2.5 text-white"
            style={{ backgroundColor: '#ff3939' }}
          >
            <span className="text-base font-bold tracking-wide flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-white flex-shrink-0" />
              PEDIDO {currentIndex + 1} DE {total} NUEVO{total !== 1 ? 'S' : ''}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                disabled={currentIndex === 0}
                className="w-7 h-7 flex items-center justify-center rounded disabled:opacity-40 transition-colors"
                style={currentIndex > 0 ? { backgroundColor: 'rgba(255,255,255,0.25)' } : undefined}
                aria-label="Anterior pedido"
              >
                <Icon icon="bxs:chevron-left" width={18} color="#ffffff" />
              </button>
              <button
                onClick={() => setCurrentIndex((i) => Math.min(total - 1, i + 1))}
                disabled={currentIndex === total - 1}
                className="w-7 h-7 flex items-center justify-center rounded disabled:opacity-40 transition-colors"
                style={currentIndex < total - 1 ? { backgroundColor: 'rgba(255,255,255,0.25)' } : undefined}
                aria-label="Siguiente pedido"
              >
                <Icon icon="bxs:chevron-right" width={18} color="#ffffff" />
              </button>
            </div>
          </div>

          {/* Card body */}
          <div className="flex flex-col" style={{ backgroundColor: '#ffffff' }}>
            <div className="px-4 py-4 flex items-center gap-4">
              {/* Delivery icon circle */}
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgba(87,189,227,0.2)' }}
              >
                <Icon icon={current.icon} color={current.accentColor} width={30} />
              </div>

              {/* Order details */}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-base leading-tight" style={{ color: '#253746' }}>
                  {current.title} {current.subtitle ?? ''}
                </p>
                <p className="text-sm mt-0.5" style={{ color: '#6B6B6B' }}>
                  {current.description}
                </p>
                {current.body ? <div className="text-sm font-semibold mt-0.5">{current.body}</div> : null}
              </div>

              {/* Action buttons */}
              <div className="flex flex-col items-stretch gap-2 flex-shrink-0 min-w-[100px]">
                {current.onAccept && (
                  <button
                    onClick={current.onAccept}
                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-bold"
                    style={{ backgroundColor: '#1fc49a', color: '#ffffff' }}
                  >
                    {(current.acceptLabel ?? 'ACEPTAR').toUpperCase()}
                    <Icon icon="mingcute:check-fill" width={15} color="#ffffff" />
                  </button>
                )}
                {current.onReject && (
                  <button
                    onClick={current.onReject}
                    className="flex items-center justify-center gap-1 py-1 text-sm font-bold"
                    style={{ color: '#FF3939' }}
                  >
                    {(current.rejectLabel ?? 'IGNORAR').toUpperCase()}
                    <Icon icon="ep:close-bold" color="#FF3939" width={12} />
                  </button>
                )}
              </div>
            </div>
            {/* Bottom accent bar */}
            <div ref={timerBarRef} className="h-[6px]" style={{ backgroundColor: '#4298B5', width: '100%' }} />
          </div>
        </div>
      )}
    </div>
  );
};
